import React, {
  MouseEventHandler,
  ComponentProps,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentOrganization } from 'spw-redux/modules/organization/selectors';
import { updateUserAvatar } from 'spw-redux/modules/user/actions';
import { selectUserAvatarUpdatedAt } from 'spw-redux/modules/user/selectors';
import { AvatarImage, SPWAvatar } from 'spw-shared/globalComponents';

type Props = {
  className?: string;
  allowUpload?: boolean;
  hasAvatar?: boolean;
  avatarPath?: string;
  userId?: number;
  onClick?: MouseEventHandler;
  testingPrefix?: string;
};

const UserAvatar = ({
  className,
  allowUpload = false,
  hasAvatar,
  avatarPath,
  userId,
  onClick,
  testingPrefix = '',
}: Props) => {
  const dispatch = useDispatch();
  const [avatarExists, setAvatarExists] = useState(hasAvatar);
  const currentOrg = useSelector(selectCurrentOrganization);
  const userAvatarUpdatedAt = useSelector(selectUserAvatarUpdatedAt);
  useEffect(() => {
    setAvatarExists(hasAvatar);
  }, [hasAvatar]);

  const handleAvatarChange = useCallback<
    ComponentProps<typeof SPWAvatar>['onChange']
  >(
    (base64) => {
      const forceUpdate = () => {
        // force an update when there was no avatar previously
        setAvatarExists(!!base64);
      };
      dispatch(
        updateUserAvatar({
          form: { id: userId, image: base64, organizationId: currentOrg?.id },
          onSuccess: forceUpdate,
        })
      );
    },
    [userId, dispatch, currentOrg?.id]
  );

  return (
    <SPWAvatar
      dataTestId={`${testingPrefix}-user-avatar`}
      alt="user-avatar"
      className={className}
      onClick={onClick}
      disableUpload={!allowUpload}
      onChange={handleAvatarChange}
      noContent={
        <AvatarImage
          src="/images/user-avatar.svg"
          alt="user-avatar"
          data-testid={`${testingPrefix}-user-avatar`}
        />
      }
      src={
        avatarExists &&
        `/users/${userId}/avatar/${avatarPath}?${userAvatarUpdatedAt}`
      }
    />
  );
};

export default UserAvatar;
